import React, { type FC } from 'react'
import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material'

import { Translation } from '../../../domain'

import { CybusMinimalLogo } from '../common'
import { createFormatter, FormattedTranslation, useTranslator } from '../Internationalization'
import { ForwardRefPermissionedLink, HOME_ROUTE, useHasCapabilities } from '../routing'
import { insetFocusOutline, THEME_SPACING_UNIT } from '../theme'
import { TOPBAR_HEIGHT } from './Constants'

/** In spacing steps */
const SIDE_PADDING = 3
const LOGO_SIZE = 22

const brandStyle: SxProps<Theme> = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 2,
    flexShrink: 0,
    width: '100%',
    height: TOPBAR_HEIGHT,
    px: SIDE_PADDING,
    color: 'grey.600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '&:focus-visible': insetFocusOutline(theme),
})

/** A logo rather than an icon, so it doesn't take the 20px icon size */
const logoStyle: SxProps<Theme> = { width: LOGO_SIZE, height: LOGO_SIZE }

/**
 * When there's no room for the name, it drops onto a second line, which the block hides, so it disappears whole and the logo
 * stays, centred in the block, instead of the name being cut. The block never gets narrower than the logo and its padding.
 */
const fittingBrandStyle: SxProps<Theme> = {
    width: 'auto',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'center',
    minWidth: `${LOGO_SIZE + 2 * SIDE_PADDING * THEME_SPACING_UNIT}px`,
}
const fittingLogoStyle: SxProps<Theme> = { my: `${(TOPBAR_HEIGHT - LOGO_SIZE) / 2}px` }
const fittingNameStyle: SxProps<Theme> = { lineHeight: `${TOPBAR_HEIGHT}px` }

/** The company name stands out from the product name, darker and semibold. The theme's typography has no semibold weight. */
const COMPANY_NAME_WEIGHT = 600
const nameStyle: SxProps<Theme> = { '& b': { fontWeight: COMPANY_NAME_WEIGHT, color: 'grey.900' } }
const companyNameFormatter = createFormatter((props) => <b {...props} />, { 'data-testid': 'navigation-brand-company' })

/**
 * The logo and product name, in a block as tall as the top bar, linking to the start page: at the top of the side bar, and in
 * the middle of the phone bar. In the side bar the logo keeps its place when the side bar closes, so it doesn't jump while
 * the width animates. When `collapsed`, only the logo shows, so the name is the link's accessible name and, like the side
 * links, a tooltip. With `hideNameWhenTight`, the name also hides by itself when the space around the brand is too narrow.
 * A user who can't open the start page gets the same block without the link.
 */
export const Brand: FC<Readonly<{ collapsed?: boolean, hideNameWhenTight?: boolean }>> = ({ collapsed = false, hideNameWhenTight = false }) => {
    const translator = useTranslator()
    const hasCapabilities = useHasCapabilities()
    const homeLabel = translator.formatTranslation(Translation.NAVIGATION_BRAND_HOME)

    const content = (
        <>
            <CybusMinimalLogo data-testid="navigation-brand-logo" sx={[logoStyle, hideNameWhenTight && fittingLogoStyle]} />
            {!collapsed && (
                <Typography component="span" variant="body2" data-testid="navigation-brand-name" sx={[nameStyle, hideNameWhenTight && fittingNameStyle]}>
                    <FormattedTranslation id={Translation.NAVIGATION_BRAND} values={{ b: companyNameFormatter }} />
                </Typography>
            )}
        </>
    )

    if (!hasCapabilities(HOME_ROUTE)) {
        return (
            <Box data-testid="navigation-brand" sx={[brandStyle, hideNameWhenTight && fittingBrandStyle]}>
                {content}
            </Box>
        )
    }

    return (
        <Tooltip title={collapsed ? homeLabel : ''} placement="right">
            <ForwardRefPermissionedLink
                data-testid="navigation-brand"
                aria-label={homeLabel}
                path={HOME_ROUTE}
                color="inherit"
                sx={[brandStyle, hideNameWhenTight && fittingBrandStyle]}
            >
                {content}
            </ForwardRefPermissionedLink>
        </Tooltip>
    )
}
